import React, { useState } from 'react';
import { CalendarIcon, MailIcon, LinkedinIcon, PhoneIcon, PlusIcon, TrashIcon, ArrowUpDownIcon, CheckCircleIcon, InfoIcon, ClockIcon } from 'lucide-react';
interface OutreachSequenceProps {
  personId: number;
  personName: string;
  initialMessage: string;
}
type SequenceStep = {
  id: number;
  channel: 'email' | 'linkedin' | 'call';
  days: number;
  message: string;
  subject?: string;
};
export const OutreachSequence: React.FC<OutreachSequenceProps> = ({
  personId,
  personName,
  initialMessage
}) => {
  const [sequence, setSequence] = useState<SequenceStep[]>([{
    id: 1,
    channel: 'linkedin',
    days: 0,
    message: initialMessage
  }]);
  const [showForm, setShowForm] = useState(false);
  const [newStep, setNewStep] = useState<Partial<SequenceStep>>({
    channel: 'email',
    days: 3,
    message: '',
    subject: ''
  });
  const [sequenceActive, setSequenceActive] = useState(false);
  const handleAddStep = () => {
    if (!newStep.message) return;
    const newId = Math.max(0, ...sequence.map(s => s.id)) + 1;
    setSequence([...sequence, {
      id: newId,
      channel: newStep.channel as 'email' | 'linkedin' | 'call',
      days: newStep.days || 0,
      message: newStep.message,
      subject: newStep.subject
    }]);
    setNewStep({
      channel: 'email',
      days: 3,
      message: '',
      subject: ''
    });
    setShowForm(false);
  };
  const handleRemoveStep = (id: number) => {
    setSequence(sequence.filter(step => step.id !== id));
  };
  const handleMoveStep = (id: number, direction: 'up' | 'down') => {
    const index = sequence.findIndex(step => step.id === id);
    if (direction === 'up' && index === 0 || direction === 'down' && index === sequence.length - 1) {
      return;
    }
    const newSequence = [...sequence];
    const step = newSequence[index];
    if (direction === 'up') {
      newSequence[index] = newSequence[index - 1];
      newSequence[index - 1] = step;
    } else {
      newSequence[index] = newSequence[index + 1];
      newSequence[index + 1] = step;
    }
    setSequence(newSequence);
  };
  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <MailIcon size={16} className="text-blue-600" data-id="element-577" />;
      case 'linkedin':
        return <LinkedinIcon size={16} className="text-blue-800" data-id="element-578" />;
      case 'call':
        return <PhoneIcon size={16} className="text-green-600" data-id="element-579" />;
      default:
        return null;
    }
  };
  const handleActivateSequence = () => {
    setSequenceActive(true);
    // In a real app, this would save the sequence to a backend
    setTimeout(() => {
      alert(`Sequence activated for ${personName}! The first message will be sent immediately.`);
    }, 500);
  };
  // Calculate the total sequence duration in days
  const totalDuration = sequence.reduce((total, step, index) => {
    if (index === 0) return 0;
    return total + step.days;
  }, 0);
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-580">
      <div className="flex justify-between items-center mb-4" data-id="element-581">
        <div data-id="element-582">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-583">
            Outreach Sequence for {personName}
          </h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-584">
            {sequenceActive ? 'Active sequence' : 'Create a multi-step outreach sequence'}
            {sequence.length > 1 && ` • ${sequence.length} steps over ${totalDuration} days`}
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md ${sequenceActive ? 'text-gray-700 bg-gray-100 hover:bg-gray-200' : 'text-white bg-[#f9b30f] hover:bg-[#e0a40e]'}`} disabled={sequenceActive} data-id="element-585">
          <PlusIcon size={16} className="mr-1" data-id="element-586" />
          {showForm ? 'Cancel' : 'Add Step'}
        </button>
      </div>

      {sequenceActive && <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-md flex items-center" data-id="element-587">
          <CheckCircleIcon className="text-green-500 mr-2" size={18} data-id="element-588" />
          <div data-id="element-589">
            <p className="text-sm text-green-700 font-medium" data-id="element-590">
              Sequence is active
            </p>
            <p className="text-xs text-green-600" data-id="element-591">
              First message sent today at 10:30 AM
            </p>
          </div>
        </div>}

      {showForm && !sequenceActive && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-592">
          <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-593">
            Add New Sequence Step
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-594">
            <div data-id="element-595">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-596">
                Channel
              </label>
              <select value={newStep.channel} onChange={e => setNewStep({
            ...newStep,
            channel: e.target.value as any
          })} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" data-id="element-597">
                <option value="linkedin" data-id="element-598">LinkedIn Message</option>
                <option value="email" data-id="element-599">Email</option>
                <option value="call" data-id="element-600">Phone Call</option>
              </select>
            </div>
            <div data-id="element-601">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-602">
                Days After Previous
              </label>
              <input type="number" min="0" value={newStep.days} onChange={e => setNewStep({
            ...newStep,
            days: parseInt(e.target.value)
          })} className="block w-full pl-3 pr-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" data-id="element-603" />
            </div>
          </div>
          {newStep.channel === 'email' && <div className="mb-4" data-id="element-604">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-605">
                Subject
              </label>
              <input type="text" value={newStep.subject || ''} onChange={e => setNewStep({
          ...newStep,
          subject: e.target.value
        })} placeholder="Email subject line" className="block w-full pl-3 pr-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" data-id="element-606" />
            </div>}
          <div className="mb-4" data-id="element-607">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-608">
              Message
            </label>
            <textarea value={newStep.message} onChange={e => setNewStep({
          ...newStep,
          message: e.target.value
        })} rows={4} placeholder="Write your message..." className="block w-full pl-3 pr-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" data-id="element-609"></textarea>
          </div>
          <div className="flex justify-end space-x-2" data-id="element-610">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50" data-id="element-611">
              Cancel
            </button>
            <button onClick={handleAddStep} disabled={!newStep.message} className="px-3 py-1.5 bg-[#f9b30f] hover:bg-[#e0a40e] text-sm font-medium rounded-md text-white disabled:opacity-50" data-id="element-612">
              Add to Sequence
            </button>
          </div>
        </div>}

      <div className="mb-4" data-id="element-613">
        <div className="relative" data-id="element-614">
          {sequence.map((step, index) => <div key={step.id} className="mb-6 relative" data-id="element-615">
              <div className="flex items-center mb-2" data-id="element-616">
                <div className="flex items-center bg-white z-10" data-id="element-617">
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center mr-2 ${sequenceActive ? index === 0 ? 'bg-green-100' : 'bg-blue-100' : 'bg-blue-100'}`} data-id="element-618">
                    {sequenceActive && index === 0 ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-619" /> : getChannelIcon(step.channel)}
                  </div>
                  <div className="font-medium" data-id="element-620">
                    {index === 0 ? 'Initial Contact' : `Follow-up #${index}`}
                    <span className="ml-2 text-sm font-normal text-gray-500" data-id="element-621">
                      {index > 0 ? `(Day ${step.days})` : '(Today)'}
                    </span>
                  </div>
                </div>
                <div className="ml-auto flex items-center" data-id="element-622">
                  {sequenceActive ? index === 0 ? <span className="text-xs text-green-600 flex items-center" data-id="element-623">
                        <CheckCircleIcon size={12} className="mr-1" data-id="element-624" />
                        Sent
                      </span> : <span className="text-xs text-gray-500 flex items-center" data-id="element-625">
                        <ClockIcon size={12} className="mr-1" data-id="element-626" />
                        Scheduled
                      </span> : <>
                      <button onClick={() => handleMoveStep(step.id, 'up')} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30" data-id="element-627">
                        <ArrowUpDownIcon size={16} data-id="element-628" />
                      </button>
                      <button onClick={() => handleRemoveStep(step.id)} disabled={index === 0} className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30" data-id="element-629">
                        <TrashIcon size={16} data-id="element-630" />
                      </button>
                    </>}
                </div>
              </div>
              {step.channel === 'email' && step.subject && <div className="ml-10 mb-1 text-sm" data-id="element-631">
                  <span className="font-medium" data-id="element-632">Subject:</span> {step.subject}
                </div>}
              <div className="ml-10 p-3 bg-gray-50 rounded-md border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap" data-id="element-633">
                {step.message}
              </div>
              {index < sequence.length - 1 && <div className="absolute left-4 top-8 bottom-0 border-l-2 border-dashed border-gray-200 -mb-4" data-id="element-634"></div>}
            </div>)}
        </div>
      </div>

      {sequence.length > 1 && !sequenceActive && <div className="bg-blue-50 p-3 rounded-md border border-blue-100 mb-4" data-id="element-635">
          <div className="flex items-start" data-id="element-636">
            <InfoIcon size={16} className="text-blue-500 mt-0.5 mr-2" data-id="element-637" />
            <div className="text-sm text-blue-700" data-id="element-638">
              <p className="font-medium mb-1" data-id="element-639">Sequence Summary</p>
              <p data-id="element-640">
                This {sequence.length}-step sequence will run over{' '}
                {totalDuration} days using{' '}
                {sequence.filter(s => s.channel === 'linkedin').length}{' '}
                LinkedIn messages,{' '}
                {sequence.filter(s => s.channel === 'email').length} emails,
                and {sequence.filter(s => s.channel === 'call').length} call
                reminders.
              </p>
            </div>
          </div>
        </div>}

      {!sequenceActive && <div className="flex justify-end mt-6" data-id="element-641">
          <button onClick={handleActivateSequence} className="px-4 py-2 bg-[#f9b30f] hover:bg-[#e0a40e] rounded-md text-sm font-medium text-white" disabled={sequence.length < 2} data-id="element-642">
            Activate Sequence
          </button>
        </div>}
    </div>;
};